import Card from "@material-ui/core/Card";
import { makeStyles } from "@material-ui/core/styles";
import SendIcon from "@material-ui/icons/Send";
import { useEffect, useRef, useState } from "react";
import { changeAppState } from "../../../../../../../../../../common/appState";
import { useVirtualScrollDummyCard } from "../../../../../../../../../../common/hooks/useVirtualScrollDummyCard";
import { spaceBetween } from "../../../../../../../../../../common/util/Array/spaceBetween";
import { fetchPost } from "../../../../../../../../../../common/util/fetch";
import { UnfriendIcon } from "../../../../../../../../../../icon/UnfriendIcon";
import { DarkButton } from "../../../../../../../../../shared/Button/DarkButton";
import { EllipsisLabel } from "../../../../../../../../../shared/EllipsisLabel/EllipsisLabel";
import { DarkPopoverMenu } from "../../../../../../../../../shared/Popover/DarkPopoverMenu";
import { openOtherUserPanel } from "../../../../../../../../../shared/User/OtherUserPanel/functions";
import { UserAvatar } from "../../../../../../../../../shared/User/UserAvatar/UserAvatar";
import {
    RelatedUser,
    User,
    UserForFriendList,
    UserForRanking,
} from "../../../../../../../../../shared/User/types";

export const myPageFriendCardScrollableContainerHeight = 308;

export function FriendRecord({
    friend,
    colorNumber,
    user,
    scrollableContainerEl,
    scrollTop,
    index,
}: {
    friend: UserForFriendList;
    user: User;
    colorNumber: number;
    scrollableContainerEl: HTMLDivElement | null;
    scrollTop: number;
    index: number;
}) {
    const c = useStyles();

    const openUserPanel = () => {
        openOtherUserPanel(friend.userId);
    };

    const [alreadyFetched, isAlreadyFetched] = useState(false);
    useEffect(() => {
        if (friend.isNewFriend && user && !alreadyFetched) {
            isAlreadyFetched(true);
            fetchMakeFriendOld(user.userId, friend.userId);
        }
    }, [friend, user]);

    const { cardRef, isDummyCard } = useVirtualScrollDummyCard(
        scrollTop,
        myPageFriendCardScrollableContainerHeight,
        cardHeight,
        index
    );

    if (isDummyCard) {
        return <div className={c.dummyCard} ref={cardRef} key="outsideCard" />;
    }

    return (
        <Card className={c.card} ref={cardRef} key="outsideCard">
            {friend.isNewFriend && (
                <Card className={spaceBetween("nowrap", c.new)}>New</Card>
            )}
            <div className={c.avatarContainer} onClick={openUserPanel}>
                <UserAvatar user={friend} colorNumber={colorNumber} size={40} />
            </div>
            <div className={c.nameAndIcons}>
                <div className={c.name} onClick={openUserPanel}>
                    <EllipsisLabel title={friend.name} placement="top" />
                </div>
                <div className={c.icons}>
                    <DarkButton
                        className={c.iconButton}
                        onClick={() => {
                            changeAppState("messagePanelState", {
                                targetUserId: friend.userId,
                            });
                        }}
                    >
                        <SendIcon />
                    </DarkButton>
                    <UnfriendIconButton
                        className={c.iconButton}
                        containerEl={scrollableContainerEl}
                        friend={friend}
                        user={user}
                    />
                </div>
            </div>
        </Card>
    );
}
const cardHeight = 50;
const useStyles = makeStyles(theme => ({
    card: {
        margin: 5,
        display: "flex",
        padding: 5,
        height: cardHeight,
        justifyContent: "center",
        width: "calc(100% - 10px)",
        position: "relative",
        overflow: "visible",
    },
    dummyCard: {
        margin: 5,
        height: cardHeight,
    },
    new: {
        position: "absolute",
        top: 0,
        left: -5,
        backgroundColor: theme.palette.secondary.main,
        color: "white",
        padding: "0 5px",
        transform: "rotate(-35deg)",
        zIndex: 1,
        fontSize: "small",
    },
    avatarContainer: {
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        marginLeft: 5,
        marginRight: 20,
        minWidth: 35,
        cursor: "pointer",
    },
    nameAndIcons: {
        display: "flex",
        alignItems: "center",
        justifyContent: "space-around",
        width: "calc(100% - 65px)",
        height: "100%",
    },
    name: {
        width: "100%",
        paddingRight: 20,
        maxWidth: "100%",
        overflow: "hidden",
        cursor: "pointer",
    },
    icons: {
        width: 70,
        marginRight: 10,
        display: "flex",
        justifyContent: "space-between",
        height: "100%",
        alignItems: "center",
    },
    iconButton: {
        minWidth: 30,
        minHeight: 30,
        maxWidth: 30,
        maxHeight: 30,
        margin: 5,
        lineHeight: 1,
    },
}));

function UnfriendIconButton({
    className,
    containerEl,
    friend,
    user,
}: {
    className: string;
    containerEl: HTMLDivElement | null;
    friend: UserForRanking;
    user: User;
}) {
    const buttonRef = useRef<HTMLButtonElement>(null);
    const [isPopoverOpen, setPopoverOpen] = useState(false);
    const [submitting, setSubmitting] = useState(false);

    return (
        <>
            <DarkButton
                ref={buttonRef}
                className={className}
                onClick={() => {
                    setPopoverOpen(!isPopoverOpen);
                }}
            >
                <UnfriendIcon />
            </DarkButton>
            <DarkPopoverMenu
                anchorEl={buttonRef.current}
                containerEl={containerEl}
                open={!!buttonRef.current && isPopoverOpen}
                onClose={() => {
                    setPopoverOpen(false);
                }}
                items={[
                    {
                        label: "Unfriend",
                        icon: <UnfriendIcon />,
                        onClick: async () => {
                            setSubmitting(true);
                            await fetchUnfriend(user.userId, friend.userId);
                            setSubmitting(false);
                        },
                    },
                ]}
                disabled={submitting}
            />
        </>
    );
}

async function fetchUnfriend(myUserId: number, toUserId: number) {
    const relatedUsers = await fetchPost<
        { myUserId: number; toUserId: number },
        RelatedUser[]
    >("api/Friend/Unfriend", { myUserId, toUserId });

    if ("error" in relatedUsers) {
        return;
    }
    changeAppState("relatedUsers", relatedUsers);
}

async function fetchMakeFriendOld(myUserId: number, friendUserId: number) {
    const relatedUsers = await fetchPost<
        { myUserId: number; friendUserId: number },
        RelatedUser[]
    >("api/Friend/MakeFriendOld", { myUserId, friendUserId });

    if ("error" in relatedUsers) {
        return;
    }
    changeAppState("relatedUsers", relatedUsers);
}
